/**
 * Table Components - Reusable table cell elements
 *
 * Common components for use within DataTable cells
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import jobAdderIcon from '@/assets/JobAdder-icon.svg';
import bullhornIcon from '@/assets/Bullhorn-icon.svg';

// Status badge with semantic colors
export interface StatusBadgeProps {
  status: string;
  variant?: 'default' | 'outline' | 'secondary' | 'destructive';
  className?: string;
}

export function StatusBadge({ status, variant, className }: StatusBadgeProps) {
  // Determine variant based on status if not provided
  const getVariant = () => {
    if (variant) return variant;

    const statusLower = status.toLowerCase();
    // Match statuses: accepted, placed, shortlisted, offer accepted
    if (
      statusLower.includes('accepted') ||
      statusLower.includes('placed') ||
      statusLower.includes('shortlisted') ||
      (statusLower.includes('offer') && statusLower.includes('accepted'))
    ) {
      return 'default';
    }
    // Match statuses: rejected, agency rejected, withdrawn
    if (
      statusLower.includes('rejected') ||
      statusLower.includes('withdrawn')
    ) {
      return 'destructive';
    }
    // Match statuses: new, interviewing, offer extended + role/candidate statuses
    if (
      statusLower.includes('new') ||
      statusLower.includes('interviewing') ||
      (statusLower.includes('offer') && statusLower.includes('extended')) ||
      statusLower.includes('draft') ||
      statusLower.includes('pending') ||
      statusLower.includes('opt-in') ||
      statusLower.includes('open')
    ) {
      return 'secondary';
    }
    // Role/candidate statuses
    if (statusLower.includes('active') || statusLower.includes('shared')) {
      return 'default';
    }
    if (statusLower.includes('removed') || statusLower.includes('stopped')) {
      return 'destructive';
    }
    return 'outline';
  };

  return (
    <Badge
      variant={getVariant()}
      className={cn(
        "font-mono text-xs uppercase tracking-wide",
        "px-2.5 py-0.5",
        className
      )}
    >
      {status}
    </Badge>
  );
}

// Audience pill for PUBLIC/PARTNER/PRIVATE indicators
export interface AudiencePillProps {
  audience: 'PUBLIC' | 'PARTNER' | 'PRIVATE' | 'COMMUNITY' | 'PROJECT' | string;
  className?: string;
}

export function AudiencePill({ audience, className }: AudiencePillProps) {
  const getStyles = () => {
    switch (audience) {
      case 'PUBLIC':
        return {
          bg: 'bg-emerald-100/80 dark:bg-emerald-900/30',
          text: 'text-emerald-700 dark:text-emerald-300',
          border: 'border-emerald-300/50 dark:border-emerald-700/50',
        };
      case 'PARTNER':
        return {
          bg: 'bg-green-100/80 dark:bg-green-900/30',
          text: 'text-green-800 dark:text-green-300',
          border: 'border-green-300/50 dark:border-green-700/50',
        };
      case 'COMMUNITY':
        return {
          bg: 'bg-blue-100/80 dark:bg-blue-900/30',
          text: 'text-blue-700 dark:text-blue-300',
          border: 'border-blue-300/50 dark:border-blue-700/50',
        };
      case 'PROJECT':
        return {
          bg: 'bg-purple-100/80 dark:bg-purple-900/30',
          text: 'text-purple-700 dark:text-purple-300',
          border: 'border-purple-300/50 dark:border-purple-700/50',
        };
      default: // PRIVATE
        return {
          bg: 'bg-slate-100/80 dark:bg-slate-800/50',
          text: 'text-slate-700 dark:text-slate-300',
          border: 'border-slate-300/50 dark:border-slate-600/50',
        };
    }
  };

  const styles = getStyles();

  return (
    <span className={cn(
      "inline-flex items-center",
      "rounded-full",
      "px-2.5 py-1",
      "font-mono text-xs font-semibold uppercase tracking-wide",
      "border",
      "shadow-sm",
      "transition-transform duration-150",
      "group-hover:scale-105",
      "motion-reduce:transform-none motion-reduce:group-hover:scale-100",
      styles.bg,
      styles.text,
      styles.border,
      className
    )}>
      {audience}
    </span>
  );
}

// Truncated text with tooltip on hover
export interface TruncatedTextProps {
  text: string;
  maxLength?: number;
  className?: string;
}

export function TruncatedText({ text, maxLength = 50, className }: TruncatedTextProps) {
  const truncated = text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  const isTruncated = text.length > maxLength;

  return (
    <span
      className={cn("block", className)}
      title={isTruncated ? text : undefined}
    >
      {truncated}
    </span>
  );
}

// Metadata text - small, muted, mono
export interface MetadataTextProps {
  children: React.ReactNode;
  className?: string;
}

export function MetadataText({ children, className }: MetadataTextProps) {
  return (
    <span className={cn(
      "font-mono text-xs text-slate-500 dark:text-slate-400",
      className
    )}>
      {children}
    </span>
  );
}

type AtsSource = 'jobadder' | 'bullhorn';

function getAtsSource(externalId?: string): AtsSource | null {
  if (!externalId) return null;

  const id = externalId.toLowerCase();
  if (id.startsWith('urn:jobadder:')) return 'jobadder';
  if (id.startsWith('urn:bullhorn:')) return 'bullhorn';
  return null;
}

export interface AtsSourceIconProps {
  externalId?: string;
  className?: string;
}

export function AtsSourceIcon({ externalId, className }: AtsSourceIconProps) {
  const source = getAtsSource(externalId);
  if (!source) return null;

  const icon = source === 'jobadder'
    ? {
        label: 'JobAdder',
        src: jobAdderIcon,
      }
    : {
        label: 'Bullhorn',
        src: bullhornIcon,
      };

  return (
    <span
      className={cn(
        'inline-flex h-5 w-5 items-center justify-center overflow-hidden rounded-sm border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900',
        className
      )}
      title={`${icon.label} imported record`}
      aria-label={`${icon.label} imported record`}
    >
      <img
        src={icon.src}
        alt={`${icon.label} icon`}
        className="h-4 w-4 object-contain"
        loading="lazy"
      />
    </span>
  );
}

// Accent color helper function for row borders
export function getStatusAccentColor(status?: string): string | undefined {
  if (!status) return undefined;

  const statusLower = status.toLowerCase();

  if (statusLower.includes('active') || statusLower.includes('shared')) {
    return '#10b981'; // emerald-500
  }
  if (statusLower.includes('pending')) {
    return '#f59e0b'; // amber-500
  }
  if (statusLower.includes('draft')) {
    return '#64748b'; // slate-500
  }
  if (statusLower.includes('removed') || statusLower.includes('stopped')) {
    return '#f43f5e'; // rose-500
  }

  return undefined;
}
